// echarts 主要逻辑 components/echarts/index.ts

import { ref, onBeforeUnmount } from 'vue'

/** ===== ① ECharts core ===== */
import { init, registerTheme, registerMap } from 'echarts/core'
import type { ECharts, EChartsOption, EChartsType, SeriesOption } from 'echarts'

/** ===== ② charts ===== */
import {
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  MapChart,
  EffectScatterChart,
  LinesChart,
  PictorialBarChart,
  CustomChart
} from 'echarts/charts'

/** ===== ④ renderer ===== */
import { CanvasRenderer } from 'echarts/renderers'

/** ===== ⑤ 一次性注册（关键！） ===== */
import * as echarts from 'echarts/core'

/** ===== ③ components ===== */
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  GraphicComponent,
  GeoComponent,
  VisualMapComponent
} from 'echarts/components'

echarts.use([
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  MapChart,
  EffectScatterChart,
  LinesChart,
  PictorialBarChart,
  CustomChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  GraphicComponent,
  GeoComponent,
  VisualMapComponent,
  CanvasRenderer
])

let _themeCounter = 0
const registeredMapSet = new Set<string>()

function debounceFn<T extends (...args: any[]) => void>(fn: T, wait = 200) {
  let t: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (t) clearTimeout(t)
    t = setTimeout(() => fn(...args), wait)
  }
}

/** 注册地图数据，避免重复注册报错 */
export function registerGeoMap(mapName: string, geoJson: Record<string, any>, specialAreas?: Record<string, any>) {
  if (!mapName || !geoJson || registeredMapSet.has(mapName)) return
  registerMap(mapName, geoJson as any, specialAreas)
  registeredMapSet.add(mapName)
}

export function hasRegisteredGeoMap(mapName: string) {
  return registeredMapSet.has(mapName)
}

export function useECharts() {
  const root = ref<HTMLElement | null>(null)
  let instance: ECharts | EChartsType | null = null
  let ro: ResizeObserver | null = null
  let registeredThemeName: string | null = null

  function _registerIfThemeObject(theme?: string | Record<string, any> | null) {
    if (!theme) return undefined
    if (typeof theme === 'string') return theme
    registeredThemeName = `__dynamic_theme_${Date.now()}_${++_themeCounter}`
    try {
      registerTheme(registeredThemeName, theme)
      return registeredThemeName
    } catch (e) {
      console.warn('registerTheme failed', e)
      return undefined
    }
  }

  function initChart(theme?: string | Record<string, any> | null, initOpts?: Record<string, any>) {
    if (!root.value) return null
    if (instance) {
      try {
        instance.dispose()
      } catch {}
      instance = null
    }

    const themeName = _registerIfThemeObject(theme)
    instance = init(root.value, (themeName ?? (theme as string | undefined)) as any, initOpts ?? {})
    return instance
  }

  function getInstance() {
    return instance as ECharts | null
  }

  function setOption(option: EChartsOption, opts?: { notMerge?: boolean; lazyUpdate?: boolean }) {
    if (!instance) return
    instance.setOption(option, opts ?? { notMerge: false, lazyUpdate: false })
  }

  function resize() {
    if (!instance) return
    try {
      instance.resize()
    } catch {}
  }

  function updateSeriesData(newSeries: { index?: number; id?: string; data: any[] }[]) {
    if (!instance) return
    const option: Partial<EChartsOption> = { series: [] as any[] }
    newSeries.forEach((s) => {
      const entry: any = { data: s.data }
      if (s.id !== undefined) entry.id = s.id
      if (typeof s.index === 'number') entry.seriesIndex = s.index
      ;(option.series! as SeriesOption[]).push(entry)
    })
    instance.setOption(option as EChartsOption, { notMerge: false, lazyUpdate: false })
  }

  function updateDataset(datasetId: string | undefined, source: any[]) {
    if (!instance) return
    const opt: Partial<EChartsOption> = {}
    if (datasetId) opt.dataset = [{ id: datasetId, source }]
    else opt.dataset = [{ source }]
    instance.setOption(opt as EChartsOption, { notMerge: false })
  }

  function dispose() {
    if (ro) {
      ro.disconnect()
      ro = null
    }
    if (instance) {
      try {
        instance.dispose()
      } catch {}
      instance = null
    }
    registeredThemeName = null
  }

  function bindAutoResize(enabled = true, ms = 200) {
    if (!root.value || !instance) return
    if (ro) {
      ro.disconnect()
      ro = null
    }
    if (!enabled) return
    ro = new ResizeObserver(
      debounceFn(() => {
        try {
          instance && instance.resize()
        } catch {}
      }, ms)
    )
    ro.observe(root.value)
  }

  onBeforeUnmount(() => dispose())

  return {
    root,
    init: initChart,
    getInstance,
    setOption,
    resize,
    dispose,
    bindAutoResize,
    updateSeriesData,
    updateDataset
  }
}
